import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { getVerifiedContracts, getContractsSummary } from '../lib/api';
import { ShareToTelegram } from '../components/ShareToTelegram';
import { useChain } from '../context/ChainContext';
import { Card, CardContent, CardHeader } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Skeleton } from '../components/ui/skeleton';
import { AddressBadge } from '../components/AddressBadge';
import {
  ShieldCheck, Coins, Layers, Search, ChevronLeft, ChevronRight, Network,
} from 'lucide-react';
import { toast } from 'sonner';

const PAGE_SIZE = 25;

// Route an indexed contract to the right multichain view (Krown → legacy pages).
const contractHref = (r) => {
  const c = r.chain || 'krown';
  if (c === 'krown') return r.kind === 'token' ? `/token/${r.address}` : `/address/${r.address}`;
  return r.kind === 'token' ? `/explorer/${c}/token/${r.address}` : `/explorer/${c}/address/${r.address}`;
};

export default function ContractsPage() {
  const { chainParam, chainInfo } = useChain();
  const [kind, setKind] = useState('all');
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const [list, sum] = await Promise.all([
        getVerifiedContracts({ kind, page, pageSize: PAGE_SIZE, chain: chainParam }),
        getContractsSummary().catch(() => null),
      ]);
      setData(list);
      setSummary(sum);
    } catch (e) {
      toast.error('Failed to load contracts');
    } finally {
      setLoading(false);
    }
  }, [kind, page, chainParam]);

  useEffect(() => { fetch(); }, [fetch]);
  useEffect(() => { setPage(1); }, [chainParam]);

  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const rows = (data?.rows || []).filter((r) => {
    if (!search.trim()) return true;
    const s = search.toLowerCase().trim();
    return (
      r.address?.toLowerCase().includes(s) ||
      r.symbol?.toLowerCase().includes(s) ||
      r.name?.toLowerCase().includes(s) ||
      r.chain?.toLowerCase().includes(s)
    );
  });

  return (
    <div className="max-w-7xl mx-auto space-y-6" data-testid="contracts-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="mono-label mb-1">Verified Registry</div>
          <h1 className="text-2xl sm:text-3xl font-black tracking-tight flex items-center gap-2">
            <ShieldCheck className="w-7 h-7 text-success" /> Verified Contracts
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Indexed tokens and DEX pools <span data-testid="active-chain-label">on <span className="text-foreground font-medium">{chainInfo?.label || 'all chains'}</span></span>
          </p>
        </div>
        <Link to="/" className="text-sm text-muted-foreground hover:text-foreground">← Back to Explorer</Link>
      </div>

      {summary && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="contracts-summary">
          <SummaryTile icon={<Network className="w-4 h-4 text-chart-1" />} label="Chains" value={summary.chain_count} sub={(summary.chains || []).slice(0, 6).join(' · ')} />
          <SummaryTile icon={<Coins className="w-4 h-4 text-warning" />} label="Verified Tokens" value={summary.tokens} sub={summary.aggregates ? `+ ${summary.aggregates} aggregates` : ''} />
          <SummaryTile icon={<Layers className="w-4 h-4 text-chart-4" />} label="DEX Pools" value={summary.pools} sub="all networks" />
          <SummaryTile icon={<ShieldCheck className="w-4 h-4 text-success" />} label="All Verified" value={(summary.tokens || 0) + (summary.pools || 0)} sub="Curated registry" />
        </div>
      )}

      <Card>
        <CardHeader className="pb-3">
          <div className="flex flex-wrap items-center gap-2 justify-between">
            <div className="flex gap-1 items-center" data-testid="kind-filter">
              {['all', 'token', 'pool'].map((k) => (
                <Button
                  key={k} size="sm"
                  variant={kind === k ? 'default' : 'outline'}
                  onClick={() => { setKind(k); setPage(1); }}
                  data-testid={`kind-${k}-btn`}
                >{k === 'all' ? 'All' : k === 'token' ? 'Tokens' : 'Pools'}</Button>
              ))}
              <ShareToTelegram kind="ctr" label="Share" className="ml-2" />
            </div>
            <div className="relative w-full sm:w-64">
              <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-muted-foreground" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search address, symbol…"
                className="pl-8 font-mono text-sm"
                data-testid="contracts-search"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-6 space-y-2">
              <Skeleton className="h-8 w-full" />
              <Skeleton className="h-8 w-full" />
              <Skeleton className="h-8 w-full" />
            </div>
          ) : rows.length === 0 ? (
            <div className="p-12 text-center text-muted-foreground text-sm">No contracts match your filter{chainParam ? ` on ${chainInfo?.label}` : ''}.</div>
          ) : (
            <div className="overflow-x-auto" data-testid="contracts-table">
              <table className="w-full text-sm min-w-[760px]">
                <thead className="bg-muted/40 text-xs uppercase tracking-wider text-muted-foreground border-b border-border">
                  <tr>
                    <th className="text-left px-4 py-2.5 font-medium">Type</th>
                    <th className="text-left px-4 py-2.5 font-medium">Symbol</th>
                    <th className="text-left px-4 py-2.5 font-medium">Name</th>
                    <th className="text-left px-4 py-2.5 font-medium">Address</th>
                    <th className="text-left px-4 py-2.5 font-medium">Chain</th>
                    <th className="text-left px-4 py-2.5 font-medium">Tags</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, i) => (
                    <tr key={`${r.address}-${i}`} className="border-b border-border/60 hover:bg-muted/30" data-testid={`contract-row-${r.address}`}>
                      <td className="px-4 py-2.5">
                        {r.kind === 'token' ? (
                          <Badge variant="outline" className="text-warning border-warning/50 text-[10px]">TOKEN</Badge>
                        ) : (
                          <Badge variant="outline" className="text-chart-4 border-chart-4/50 text-[10px]">POOL</Badge>
                        )}
                      </td>
                      <td className="px-4 py-2.5 font-medium">
                        <Link to={contractHref(r)} className="hover:text-primary" data-testid={`contract-link-${r.address}`}>{r.symbol || '—'}</Link>
                      </td>
                      <td className="px-4 py-2.5 text-muted-foreground">{r.name || '—'}</td>
                      <td className="px-4 py-2.5 text-xs"><AddressBadge address={r.address} truncate link={false} /></td>
                      <td className="px-4 py-2.5 text-xs">
                        <Badge variant="outline" className="text-[10px] uppercase">{r.chain || 'krown'}</Badge>
                      </td>
                      <td className="px-4 py-2.5 text-xs">
                        <div className="flex items-center gap-1 flex-nowrap whitespace-nowrap">
                          <Badge variant="outline" className="border-success/50 text-success text-[10px]">
                            <ShieldCheck className="w-3 h-3 mr-0.5" />VERIFIED
                          </Badge>
                          {r.is_native && <Badge variant="outline" className="text-warning border-warning/50 text-[10px]">NATIVE</Badge>}
                          {r.is_price_reference && <Badge variant="outline" className="text-destructive border-destructive/50 text-[10px]">PRICE REF</Badge>}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
        {totalPages > 1 && (
          <div className="border-t border-border px-4 py-3 flex items-center justify-between">
            <div className="text-xs text-muted-foreground">{total.toLocaleString()} contracts</div>
            <div className="flex items-center gap-1">
              <Button size="sm" variant="ghost" disabled={page <= 1 || loading}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                data-testid="prev-page-btn" className="h-7 px-2"><ChevronLeft className="w-4 h-4" /></Button>
              <span className="text-xs text-muted-foreground px-2" data-testid="page-indicator">{page} / {totalPages}</span>
              <Button size="sm" variant="ghost" disabled={page >= totalPages || loading}
                onClick={() => setPage((p) => p + 1)}
                data-testid="next-page-btn" className="h-7 px-2"><ChevronRight className="w-4 h-4" /></Button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}

function SummaryTile({ icon, label, value, sub }) {
  return (
    <div className="border border-border bg-card p-3">
      <div className="flex items-center gap-2 text-xs text-muted-foreground">{icon}<span>{label}</span></div>
      <div className="text-2xl font-bold font-mono mt-1">{Number(value || 0).toLocaleString()}</div>
      {sub && <div className="text-[10px] uppercase tracking-wider text-muted-foreground mt-0.5 truncate">{sub}</div>}
    </div>
  );
}
